import {
  analyticsItemTypeByAttributesItemType,
  assigneeType,
  emptyField,
  screenType,
  statusSetsMap,
  superPropsFieldsMapping,
  reportFormats
} from '../constants/analytics';
import lodashGet from 'lodash/get';
import startCase from 'lodash/startCase';
import AnalyticsSDK, {
  LOG_LEVELS,
  MANDATORY_SUPER_PROPERTIES,
  MANDATORY_SUPER_PROPERTIES_ALLOWED_VALUES,
  SUPPORTED_TARGETS
} from '@adsk/bim360-analytics-sdk-js';
import {HQ_COMPANY_TYPE, HQ_ROLE_TYPE, HQ_USER_TYPE} from '../constants/hq-types';
import {ENV_CONFIGS} from '../constants/envs';
import {viewTypes} from '../constants/view-types';
import {ISSUE_ATTRIBUTES_DATA_TYPES, ISSUE_ATTRIBUTES_MAPPED_ITEM_TYPES} from '../constants/issue-attributes';
import {AdminPermissionsV2Types} from '../constants/issues-admin';
import {isFedrampRegion} from './region-helpers';

export const FIELD_TRACK_EVENT = 'Field';
export const ISSUES_TRACK_EVENT = 'Issues';
export const BULK = 'Bulk';
export const SINGLE = 'Single';
/* General analytics configuration for analyticsSDK */
const staticMandatorySuperProperties = (region, language) => ({
  [MANDATORY_SUPER_PROPERTIES.PLATFORM]: MANDATORY_SUPER_PROPERTIES_ALLOWED_VALUES.PLATFORM.WEB,
  [MANDATORY_SUPER_PROPERTIES.ENVIRONMENT]: ENV_CONFIGS.ANALYTICS.ENV,
  [MANDATORY_SUPER_PROPERTIES.DATA_CENTER]: region,
  [MANDATORY_SUPER_PROPERTIES.USED_LANGUAGE_CODE]: language,
  [MANDATORY_SUPER_PROPERTIES.FEATURE_MODULE]: 'Issues'
});

export const createAnalyticsManager = (region, locale) => {
  if (ENV_CONFIGS.ANALYTICS && !isFedrampRegion()) {
    // Initialize the analyticsManager
    return AnalyticsSDK.init({
      moduleName: ENV_CONFIGS.ANALYTICS.MODULE_NAME,
      targets: {[SUPPORTED_TARGETS.MIXPANEL]: {token: ENV_CONFIGS.ANALYTICS.MIXPANEL.TOKEN}},
      logLevel: ENV_CONFIGS.ANALYTICS.ENV.toLowerCase() !== 'prod' ? LOG_LEVELS.INFO : LOG_LEVELS.ERROR,
      mandatorySuperProps: staticMandatorySuperProperties(region, locale),
      disableAutoSessionStartEvent: true,
      disableAutoResetSuperProperties: true
    });
  }

  return null;
};

export const isAutomationUser = mail => {
  const automationUserNamesList = ['bimqapmauto', 'bim360auto', 'bim360applausea', 'bim360mobile', '@for-each.com'];

  return automationUserNamesList.some(string => mail.includes(string));
};

export const booleanToYesNo = bool => (bool ? 'Yes' : 'No');

const boolToActiveInactiveAction = isActivate => (isActivate ? 'Activate' : 'Deactivate');

export const getAnalyticsMandatoryPeopleProps = user => ({
  firstName: user.firstName,
  lastName: user.lastName,
  userMail: user.email,
  isEmployee: isAutomationUser(user.email)
});

export const getAnalyticsPeopleProps = user => ({
  nonSensitive: {
    Roles: user.roles && user.roles.map(({name}) => name).join(', ')
  }
});

export const viewTypeToScreenType = viewType => {
  switch (viewType) {
    case viewTypes.PROJECT:
      return screenType.PROJECT;
    case viewTypes.DOCUMENT:
      return screenType.PANEL;
    default:
      return screenType.OTHER;
  }
};

export const getFileNameExtension = fileName => {
  if (!fileName) {
    return emptyField;
  }

  const fileNameArray = fileName.split('.');
  if (fileNameArray.length === 1) {
    // Return empty string if we can't extract the extension from the filename
    return '';
  }

  return fileNameArray.pop().toUpperCase();
};

export const registerAnalyticsSuperPropsHelper = (currentProject, region, hostedBy = '') => ({
  [MANDATORY_SUPER_PROPERTIES.ACCOUNT_ID]:
    lodashGet(currentProject, 'account.id') || lodashGet(currentProject, 'accountId'),
  [MANDATORY_SUPER_PROPERTIES.ACCOUNT_NAME]:
    lodashGet(currentProject, 'account.name') || lodashGet(currentProject, 'accountName'),
  [MANDATORY_SUPER_PROPERTIES.PROJECT_ID]: currentProject.id,
  [MANDATORY_SUPER_PROPERTIES.PROJECT_NAME]: currentProject.name,
  [MANDATORY_SUPER_PROPERTIES.DATA_CENTER]: region,
  [superPropsFieldsMapping.HOSTED_BY]: hostedBy
});

export const getAssigneeTypeByAssigneeId = (currentAssigneeId, currentUserId, oxygen, assignedToType) => {
  if (currentAssigneeId && assignedToType) {
    const currentUserCompanyId = lodashGet(oxygen, `users[${currentUserId}].company.oxygenId`);
    switch (assignedToType) {
      case HQ_USER_TYPE:
        if (currentUserId === currentAssigneeId) {
          return assigneeType.ME;
        } else if (lodashGet(oxygen, `users[${currentAssigneeId}].company.oxygenId`) === currentUserCompanyId) {
          return assigneeType.MY_COMPANY;
        }

        return assigneeType.USER;
      case HQ_COMPANY_TYPE:
        if (currentUserCompanyId === currentAssigneeId) {
          return assigneeType.MY_COMPANY;
        }

        return assigneeType.COMPANY;
      case HQ_ROLE_TYPE: {
        const userRolesIds = lodashGet(oxygen, `users[${currentUserId}].roles`, []).map(role => role.oxygenId);
        if (userRolesIds.includes(currentAssigneeId)) {
          return assigneeType.MY_ROLE;
        }

        return assigneeType.ROLE;
      }
      default:
        return assigneeType.OTHER;
    }
  } else {
    return assigneeType.UNASSIGNED;
  }
};

export const entityPreview = (document, status, origin, assignee, issueTypeTitle, issueSubTypeTitle) => ({
  eventName: 'View Issue',
  properties: {
    Extension: getFileNameExtension(lodashGet(document, 'fileName')),
    Status: status ? startCase(status) : emptyField,
    Origin: origin,
    Assignee: assignee,
    Type: issueTypeTitle,
    'Sub-type': issueSubTypeTitle
  }
});

export const createIssue = (
  document,
  status,
  origin,
  issuePushpin,
  uploadingAttachments,
  issueTypeTitle,
  issueSubTypeTitle,
  assignee,
  createAnother,
  numberOfCustomAttributes,
  numberOfUsedCustomAttributes,
  isFromTemplate
) => ({
  eventName: 'Create Issue',
  properties: {
    Extension: getFileNameExtension(lodashGet(document, 'fileName')),
    Status: status ? startCase(status) : emptyField,
    Origin: origin || screenType.OTHER,
    Pushpin: booleanToYesNo(issuePushpin),
    Attachments: uploadingAttachments ? uploadingAttachments.length : emptyField,
    Type: issueTypeTitle,
    'Sub-type': issueSubTypeTitle,
    Assignee: assignee,
    'Create Another': booleanToYesNo(createAnother),
    'Number Of Custom Attributes': numberOfCustomAttributes,
    'Number Of Used Custom Attributes': numberOfUsedCustomAttributes,
    'Issue Template': booleanToYesNo(isFromTemplate)
  }
});

export const createIssueTemplate = (templateCategoryTitle, issueTypeTitle, assignee, hasDueDate) => ({
  eventName: 'Create Template',
  properties: {
    Category: templateCategoryTitle,
    Type: issueTypeTitle,
    Assignee: assignee,
    'Due Date': booleanToYesNo(hasDueDate)
  }
});

export const entityIssueScreen = origin => ({
  eventName: 'Issues Screen',
  properties: {
    Origin: origin
  }
});

export const addComment = (issueTypeTitle, issueSubTypeTitle, isMention) => ({
  eventName: 'Add Issue Comment',
  properties: {
    Type: issueTypeTitle,
    'Sub-type': issueSubTypeTitle,
    Mention: booleanToYesNo(isMention)
  }
});

export const addAttachment = (attachmentType, fileName, issueTypeTitle, issueSubTypeTitle) => ({
  eventName: 'Add Issue Attachment',
  properties: {
    'Attachment Type': attachmentType,
    Extension: getFileNameExtension(fileName),
    Type: issueTypeTitle,
    'Sub-type': issueSubTypeTitle
  }
});

export const fieldEdit = (fieldName, issueTypeTitle, issueSubTypeTitle) => ({
  eventName: 'Edit Issue',
  properties: {
    'Changed Attribute': startCase(fieldName),
    Type: issueTypeTitle,
    'Sub-type': issueSubTypeTitle
  }
});

export const statusChange = (
  oldStatus,
  newStatus,
  issueTypeTitle,
  issueSubTypeTitle,
  isChangingAssigneeOnStatusChange,
  isAddingCommentOnStatusChange
) => {
  const oldStatusInStartCase = oldStatus ? startCase(oldStatus) : emptyField;
  const newStatusInStartCase = newStatus ? startCase(newStatus) : emptyField;
  return {
    eventName: 'Change Issue Status',
    properties: {
      'Previous Status': oldStatusInStartCase,
      Status: newStatusInStartCase,
      'Change Assignee': booleanToYesNo(isChangingAssigneeOnStatusChange),
      'Add Comment': booleanToYesNo(isAddingCommentOnStatusChange),
      Type: issueTypeTitle,
      'Sub-type': issueSubTypeTitle
    }
  };
};

export const assigneeChange = (entityType, issueTypeTitle, issueSubTypeTitle, prevAssignee, newAssignee) => ({
  eventName: 'Change Issue Assignee',
  properties: {
    Assignee: newAssignee,
    'Previous Assignee': prevAssignee,
    Type: issueTypeTitle,
    'Sub-type': issueSubTypeTitle
  }
});

export const downloadReportAnalytics = (assignee, origin, status, issueTypeTitle, issueSubTypeTitle) => {
  const properties = {
    Assignee: assignee,
    Origin: origin,
    Status: startCase(status),
    Type: issueTypeTitle,
    'Sub-type': issueSubTypeTitle
  };

  return {
    eventName: 'Download Issue',
    properties
  };
};

export const deleteAttachment = (viewType, assignee, isOwner, status) => {
  const statusInStartCase = status ? startCase(status) : emptyField;
  const origin = viewTypeToScreenType(viewType);

  const properties = {
    Assignee: assignee,
    Origin: origin,
    Status: statusInStartCase,
    'Own Attachment': booleanToYesNo(isOwner)
  };

  return {
    eventName: 'Delete Issue Attachment',
    properties
  };
};

export const issueTypesAdminVisit = () => ({
  eventName: 'Admin - Types Screen'
});

export const issueTypeCreate = (isActive, title, statusSet) => ({
  eventName: 'Admin - Types - Create Issue Type',
  properties: {
    Active: booleanToYesNo(isActive),
    'Type Name': title,
    'Status Set': statusSetsMap[statusSet]
  }
});

export const issueSubTypeCreate = (isParentStandard, isActive, title, parentTitle) => ({
  eventName: 'Admin - Types - Create Issue Sub-type',
  properties: {
    'Parent Is Standard': booleanToYesNo(isParentStandard),
    Active: booleanToYesNo(isActive),
    'Sub-type Name': title,
    'Type Name': parentTitle
  }
});

export const issueAllIssuesTypesModalVisit = () => ({
  eventName: 'Admin - Types - All Issues Types'
});

export const issueAllIssuesTypesUpdateRequiredFields = ({active}) => ({
  eventName: 'Admin - Types - All Issues Types - Required OOTB',
  properties: {
    Active: active
  }
});

export const issueTypeSetStatusChanged = (isStandard, isActive) => ({
  eventName: 'Admin - Types - Change Type Status',
  properties: {
    'Is Standard': booleanToYesNo(isStandard),
    Action: boolToActiveInactiveAction(isActive)
  }
});

export const issueSubTypeSetStatusChanged = (isStandard, isParentStandard, isActive) => ({
  eventName: 'Admin - Types - Change Sub-type Status',
  properties: {
    'Is Standard': booleanToYesNo(isStandard),
    'Parent Is Standard': booleanToYesNo(isParentStandard),
    Action: boolToActiveInactiveAction(isActive)
  }
});

export const issueTypeMapCustomAttribute = (itemType, itemName, totalNumberOfAttributes) => ({
  eventName: 'Admin - Types - Map Custom Attributes',
  properties: {
    Level: analyticsItemTypeByAttributesItemType[itemType],
    Name:
      itemType === ISSUE_ATTRIBUTES_MAPPED_ITEM_TYPES.CONTAINER
        ? analyticsItemTypeByAttributesItemType[itemType]
        : itemName,
    'Number Of Attributes': totalNumberOfAttributes
  }
});

export const issueAttributesAdminVisit = () => ({
  eventName: 'Admin - Attributes Screen'
});

export const issueAttributesCreate = ({dataType, description, title, createAnother}) => {
  let dataTypeName = '';
  switch (dataType) {
    default:
    case ISSUE_ATTRIBUTES_DATA_TYPES.TEXT:
      dataTypeName = 'Text';
      break;
    case ISSUE_ATTRIBUTES_DATA_TYPES.SINGLE_LIST:
      dataTypeName = 'Dropdown';
      break;
    case ISSUE_ATTRIBUTES_DATA_TYPES.NUMERIC:
      dataTypeName = 'Numeric';
      break;
    case ISSUE_ATTRIBUTES_DATA_TYPES.PARAGRAPH:
      dataTypeName = 'Paragraph';
      break;
  }
  return {
    eventName: 'Admin - Attributes - Create Custom Attribute',
    properties: {
      Type: dataTypeName,
      Description: booleanToYesNo(!!description),
      'Attribute Name': title,
      'Create Another': booleanToYesNo(createAnother)
    }
  };
};

export const issueAttributesUpdate = (titleEdited, descriptionEdited) => ({
  eventName: 'Admin - Attributes - Edit Custom Attribute',
  properties: {
    'Edit Name': booleanToYesNo(titleEdited),
    'Edit Description': booleanToYesNo(descriptionEdited)
  }
});

export const issueAttributesDelete = () => ({
  eventName: 'Admin - Attributes - Delete Custom Attribute'
});

export const issuePermissionsAdminVisit = () => ({
  eventName: 'Admin - Permissions Screen'
});

export const exportList = (reportFormat, viewType, type, issueCount) => ({
  eventName: 'Export Issue List',
  properties: {
    Format: reportFormat,
    Origin: viewTypeToScreenType(viewType),
    ...(reportFormat === reportFormats.BCF_V2 && type === BULK && {'Issues Count': issueCount}),
    ...(reportFormat === reportFormats.BCF_V2 && {'Type': type})
  }
});

export const issueRootCausesAdminVisit = () => ({
  eventName: 'Admin - Root Causes Screen'
});

export const issueRootCauseCategorySetStatusChanged = (isStandard, isActive) => ({
  eventName: 'Admin - Root Causes - Change Category Status',
  properties: {
    'Is Standard': booleanToYesNo(isStandard),
    Action: boolToActiveInactiveAction(isActive)
  }
});

export const issueRootCauseSetStatusChanged = (isStandard, isParentStandard, isActive) => ({
  eventName: 'Admin - Root Causes - Change Root Cause Status',
  properties: {
    'Is Standard': booleanToYesNo(isStandard),
    'Is Standard Category': booleanToYesNo(isParentStandard),
    Action: boolToActiveInactiveAction(isActive)
  }
});

export const issueRootCauseCategoryCreate = (isActive, title) => ({
  eventName: 'Admin - Root Causes - Create Category',
  properties: {
    Active: booleanToYesNo(isActive),
    'Category Name': title
  }
});

export const issueRootCauseCreate = (isParentStandard, isActive, title, parentTitle) => ({
  eventName: 'Admin - Root Causes - Create Root Cause',
  properties: {
    'Is Standard Category': booleanToYesNo(isParentStandard),
    Active: booleanToYesNo(isActive),
    'Root Cause Name': title,
    'Category Name': parentTitle
  }
});

export const issueRootCauseDelete = () => ({
  eventName: 'Admin - Root Causes - Delete Root Cause'
});

export const issueRootCauseCategoryDelete = () => ({
  eventName: 'Admin - Root Causes - Delete Category'
});

const getPermissionLevelText = permissionType => {
  switch (permissionType) {
    case AdminPermissionsV2Types.VIEW_ALL:
      return 'View All';
    case AdminPermissionsV2Types.CREATE:
      return 'Create';
    case AdminPermissionsV2Types.VIEW_AND_CREATE:
      return 'View And Create';
    case AdminPermissionsV2Types.FULL_CONTROL:
      return 'Full Control';
    default:
      return 'Other';
  }
};

export const issueAddPermission = (numberOfUsers = 0, numberOfCompanies = 0, numberOfRoles = 0, permissionType) => ({
  eventName: 'Admin - Permissions - Add Permission',
  properties: {
    'Total Entries': numberOfUsers + numberOfCompanies + numberOfRoles,
    'Number Of Users': numberOfUsers,
    'Number Of Companies': numberOfCompanies,
    'Number Of Roles': numberOfRoles,
    'Permission Level': getPermissionLevelText(permissionType)
  }
});

export const openAddReferenceModal = viewType => ({
  eventName: 'Reference Picker Bim360 - Open',
  properties: {
    Origin: viewTypeToScreenType(viewType)
  }
});

export const linkEntityToIssue = entityType => ({
  eventName: 'Reference Picker Bim360 - Link To An Issue',
  properties: {
    Entity: entityType
  }
});

export const linkIssueToDocument = viewType => ({
  eventName: 'Link Docs To An Issue',
  properties: {
    Origin: viewTypeToScreenType(viewType)
  }
});

export const unlinkDocumentFromIssue = viewType => ({
  eventName: 'Unlink Doc From An Issue',
  properties: {
    Origin: viewTypeToScreenType(viewType)
  }
});
